import ReactGA from 'react-ga4';
import { useState } from "react";
import SuccessIcon from "../../../assets/icons/success.svg?react";
import { ClientQuizFormType } from "./ClientQuiz";
import { HowDidYouHearAboutUs } from "../patient.types";
import { useNavigate } from "react-router-dom";
import FilledCheckboxIcon from "../../../assets/icons/checkbox-filled.svg?react";
import AlertIcon from "../../../assets/icons/Alert.svg?react";
import { setPartialToken, setUser, validateEmail, validatePhone } from "../../../services/utils";
import { api } from "../../../services/api";
import { TextInput } from '../../../components/TextInput';
import { SelectInput } from '../../../components/SelectInput';
import { Button } from '../../../components/Button';
import { WEBSITE_URL } from '../../../env';
import { useProvider } from '../../providers/context/provider-context';

export function QuizCreatePartialSignup({ clientQuizForm }: { clientQuizForm: ClientQuizFormType; }) {
  const { providers } = useProvider() as any;
  const [error, setError] = useState<string>("");
  const [errors, setErrors] = useState({} as any);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<{
    providerId?: string;
    guardianFirstName: string;
    guardianLastName: string;
    mobile: string;
    email: string;
    howDidYouHearAboutUs?: HowDidYouHearAboutUs;
    howDidYouHearAboutUsText?: string;
    terms: boolean;
  }>({
    providerId: undefined,
    guardianFirstName: "",
    guardianLastName: "",
    mobile: "",
    email: "",
    howDidYouHearAboutUs: undefined,
    howDidYouHearAboutUsText: "",
    terms: false,
  });
  const navigate = useNavigate();

  const textReasonRequiredHeardAboutUs = form.howDidYouHearAboutUs && [
    HowDidYouHearAboutUs.ReferralFromAnotherCareProvider,
    HowDidYouHearAboutUs.HeadstartCareProvider,
    HowDidYouHearAboutUs.SchoolOrDaycare,
    HowDidYouHearAboutUs.ParentSupportGroupOrResourceCenter,
    HowDidYouHearAboutUs.Other].includes(form.howDidYouHearAboutUs)

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "mobile") {
      // allow only number
      const regex = /^[0-9\b]+$/;
      if (value === "" || regex.test(value)) {
        if (value && !validatePhone(value.toString())) {
          setErrors((prev) => ({
            ...prev,
            mobile: "Invalid mobile number",
          }));
        } else {
          setErrors((prev) => ({ ...prev, mobile: "" }));
        }
        setForm((prev) => ({ ...prev, [name]: value }));
        return;
      } else {
        return;
      }
    }

    if (name === "email") {
      if (value && !validateEmail(value)) {
        setErrors((prev) => ({
          ...prev,
          email: "Invalid email format",
        }));
      } else {
        setErrors((prev) => ({ ...prev, email: "" }));
      }
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      const result = await api.createPartialClientSignup({
        providerId: form.providerId,
        guardianFirstName: form.guardianFirstName,
        guardianLastName: form.guardianLastName,
        email: form.email,
        mobile: form.mobile,
        howDidYouHearAboutUs: form.howDidYouHearAboutUs,
        howDidYouHearAboutUsText: form.howDidYouHearAboutUsText,
        quizAnswers: clientQuizForm,
      })

      if (result.data.id) {
        if (import.meta.env.VITE_ENV === "prod") {
          ReactGA.event("quiz_completed_partial_submitted", {
            autismDiagnosis: clientQuizForm.autismDiagnosis,
          });
        }
        setPartialToken(result.data.id)
        navigate(`/providers?zip=${clientQuizForm.zipCode}`)
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || "Something went wrong")
    }
  }

  const disabled = !form.terms || !form.guardianFirstName || !form.guardianLastName || !form.email || !form.mobile || !validateEmail(form.email) || !validatePhone(form.mobile)

  return (
    <>
      <div className="bg-white p-8 rounded-[16px]">
        <div className="grid grid-cols-2">

          <div className="col-span-2 lg:col-span-1 lg:mt-1">
            <SuccessIcon className="w-16 lg:w-48 h-auto max-h-96 mx-auto lg:my-4" />
            <h1 className="text-lg md:text-xl lg:text-3xl text-center lg:text-left font-bold mt-1 lg:mt-8 mb-2">Looks like we can help!</h1>
            <p className="hidden lg:block text-sm md:text-base text-left mb-4 mr-10">
              Based on your answers, it looks like you're a great fit for our services.
              Enter your information and reach out to a provider for a consultation in minutes.
            </p>
          </div>
          <div className="col-span-2 lg:col-span-1 mb-3">
            <form className="mt-4 flex flex-col gap-[8px] sm:gap-[22px]" onSubmit={handleSubmit}>

              <div className="flex flex-col md:flex-row items-center gap-3">
                <div className="flex-1 w-full flex flex-col gap-1.5">
                  <label className="text-sm text-black/80">
                    Parent / Guardian First Name
                  </label>
                  <TextInput
                    name="guardianFirstName"
                    value={form.guardianFirstName}
                    onChange={handleChange}
                    placeholder="Enter your first name"
                    maxLength={128}
                  />
                  <div style={{ height: "10px" }} />
                </div>
                <div className="flex-1 w-full flex flex-col gap-1.5">
                  <label htmlFor="password" className="text-sm text-black/80">
                    Parent / Guardian Last Name
                  </label>
                  <TextInput
                    name="guardianLastName"
                    value={form.guardianLastName}
                    onChange={handleChange}
                    placeholder="Enter your last name"
                    maxLength={128}
                  />
                  <div style={{ height: "10px" }} />
                </div>
              </div>

              <div className="flex flex-col md:flex-row items-center sm:gap-3 mt-[-10px] lg:mt-8">
                <div className="flex-1 w-full flex flex-col gap-0.5">
                  <label htmlFor="email" className="text-sm text-black/80">
                    Email
                  </label>
                  <TextInput
                    type="email"
                    placeholder="Enter your email address"
                    value={form.email}
                    onChange={handleChange}
                    name="email"
                    maxLength={128}
                  />
                  {errors.email && (
                    <span className="text-red-500 text-xs font-medium mt-2 flex items-center gap-1" style={{ height: "10px" }} >
                      {errors.email}
                    </span>
                  )}
                  {!errors.email && <div style={{ height: "18px" }} />}
                </div>

                <div className="flex-1 w-full flex flex-col gap-0.5">
                  <label htmlFor="email" className="text-sm text-black/80">
                    Phone Number
                  </label>
                  <TextInput
                    type="tel"
                    placeholder="Enter your phone number"
                    value={form.mobile}
                    onChange={handleChange}
                    name="mobile"
                    maxLength={10}
                  />
                  {errors.mobile && (
                    <span className="text-red-500 text-xs font-medium mt-2 flex items-center gap-1" style={{ height: "10px" }} >
                      {errors.mobile}
                    </span>
                  )}
                  {!errors.mobile && <div style={{ height: "18px" }} />}
                </div>
              </div>

              {/* <div>
                <label className="text-sm text-black/80 mb-[-15px]">
                  How did you hear about Headstart?
                </label>
                <SelectInput
                  data={Object.values(HowDidYouHearAboutUs).map((item) => ({
                    label: item,
                    value: item,
                  }))}
                  name="type"
                  value={form.howDidYouHearAboutUs}
                  disabledOptionText={"Select how you heard about Headstart"}
                  onChange={(e) => {
                    setForm({ ...form, howDidYouHearAboutUs: e.target.value as HowDidYouHearAboutUs });
                  }}
                  className={`mt-0 ${!form.howDidYouHearAboutUs && "text-slate-400"}`}
                />
                {errors.howDidYouHearAboutUs && (
                  <div className="text-red-500 text-xs font-medium mt-2 flex items-center gap-1">
                    {errors.howDidYouHearAboutUs}
                  </div>
                )}
              </div> */}

              {/* {textReasonRequiredHeardAboutUs && (
                <div className="flex-1 w-full flex flex-col gap-1.5">
                  <label htmlFor="email" className="text-sm text-black/80">
                    {form.howDidYouHearAboutUs === HowDidYouHearAboutUs.HeadstartCareProvider
                      && "What is the name of the Headstart care provider that referred you?"}
                    {form.howDidYouHearAboutUs === HowDidYouHearAboutUs.ReferralFromAnotherCareProvider
                      && "What is the name of the institution or provider that referred you? (e.g. Happy Family Pediatrics, or Dr. John Smith)"}
                    {form.howDidYouHearAboutUs === HowDidYouHearAboutUs.SchoolOrDaycare
                      && "What is the name of the facility that referred you? (e.g. Sunny Horizons Daycare)"}
                    {form.howDidYouHearAboutUs === HowDidYouHearAboutUs.ParentSupportGroupOrResourceCenter
                      && "What is the name of the group or resource center?"}
                    {form.howDidYouHearAboutUs === HowDidYouHearAboutUs.Other
                      && "Other (please specify):"}
                  </label>
                  {form.howDidYouHearAboutUs === HowDidYouHearAboutUs.HeadstartCareProvider ?
                    <SelectInput
                      data={providers.map((provider) => ({
                        label: provider.firstName + " " + provider.lastName,
                        value: provider.firstName + " " + provider.lastName,
                      }))}
                      name="howDidYouHearAboutUsText"
                      value={form.howDidYouHearAboutUsText}
                      disabledOptionText={"Name of referring Headstart care provider?"}
                      onChange={handleChange}
                      className={`mt-0 ${!form.howDidYouHearAboutUsText && "text-slate-400"}`}
                    /> :
                    <TextInput
                      type="text"
                      placeholder=""
                      value={form.howDidYouHearAboutUsText}
                      onChange={handleChange}
                      name="howDidYouHearAboutUsText"
                      maxLength={128}
                    />}
                  {errors.howDidYouHearAboutUsText && (
                    <div className="text-red-500 text-xs font-medium mt-2 flex items-center gap-1">
                      {errors.howDidYouHearAboutUsText}
                    </div>
                  )}
                </div>
              )} */}

              <div className="flex items-center gap-1.5 mt-3">
                <div className="flex">
                  <input
                    type="checkbox"
                    id="terms"
                    name="terms"
                    checked={form.terms}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        terms: e.target.checked,
                      }))
                    }
                    className={`w-[22px] h-[22px] cursor-pointer ${form.terms ? "opacity-[0] !h-0 !w-0" : ""
                      }`}
                  />
                  {form.terms ? (
                    <button
                      onClick={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          terms: false,
                        }))
                      }
                    >
                      <FilledCheckboxIcon className="w-[22px] h-[22px]" />
                    </button>
                  ) : null}
                </div>
                <label htmlFor="terms" className="text-base text-[#1D1E1B]">
                  I consent to Headstart's{" "}
                  <a
                    href={`${WEBSITE_URL}/privacy-policy`}
                    className="text-primary font-medium hover:text-black"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </a>
                </label>
              </div>

              <div className="mt-3">
                {error && (
                  <div className="text-red-500 text-xs font-medium mb-4 flex items-center gap-1">
                    <AlertIcon /> {error}
                  </div>
                )}
                <Button
                  type="submit"
                  variant="primary"
                  className="!rounded-full w-full text-xl"
                  disabled={disabled || saving}
                  loading={saving}
                >
                  Browse Providers
                </Button>
              </div>
            </form>
          </div>

        </div>
      </div>
    </>
  );
}
